/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable no-underscore-dangle */
import { reactive } from 'vue'

import Shopify from '@jackboxgames/shopify'
import { GtmSupport, useGtm } from '@gtm-support/vue-gtm'

import currencyCodeRegionMapJson from '$components/shopping/currencyCodeCloudfrontRegionMap.json'
import { I18n } from '$services/i18n'

declare module '@vue/runtime-core' {
    interface ComponentCustomProperties {
        $shopify: {
            fetchCart(): Promise<void>
            addItemToCart(
                item: Shopify.Shopify.LineItem,
                qty?: number,
                product?: Shopify.Shopify.Product,
                variant?: Shopify.Shopify.Variant
            ): Promise<void>,
            createLineItemAndAddToCart(
                product?: Shopify.Shopify.Product,
                variant?: Shopify.Shopify.Variant,
                qty?: number
            ): Promise<void>,
            updateItemQuantity(item: Shopify.Shopify.Line, quantity: number): Promise<void>
            removeItemFromCart(lineItemId: string): Promise<void>
            addItemToCartByVariantId(variantId: string): Promise<void>
            swapItemsByVariantId(oldVariantId: string, newVariantId: string): Promise<void>
            refreshCart(): Promise<void>
            updateListrakCart(): void
            getCheckoutUrl(): string | null
            fetchCollection(id: number): Promise<Shopify.Shopify.Collection>
            fetchProduct(slug: string): Promise<Shopify.Shopify.Product>
            formatDecimalPlaces(priceAmount: string, locale: string): string
        }
    }
}

export const currentCountry = reactive({
    code: 'US' // default to US
})

// Cart Tray UI Reactive Elements
export const ShoppingCart = reactive({
    _open: false,
    _items: [] as JBGShopify.DisplayItem[],
    _subtotal: {} as JBGShopify.DisplayPrice,
    _qty: 0,
    _checkoutUrl: '',
    // eslint-disable-next-line max-len
    updateCart(newItems: JBGShopify.DisplayItem[], newSubtotal: JBGShopify.DisplayPrice, newQty: number, newCheckoutUrl: string): void {
        this._items = newItems
        this._subtotal = newSubtotal
        this._qty = newQty
        this._checkoutUrl = newCheckoutUrl
    },
    getCart(): JBGShopify.DisplayItem[] {
        return this._items
    },
    getSubtotal(): JBGShopify.DisplayPrice {
        return this._subtotal
    },
    getQty(): number {
        return this._qty
    },
    getCheckoutUrl(): string {
        return this._checkoutUrl
    },
    isOpen(): boolean {
        return this._open
    },
    open(): void {
        this._open = true
        setTimeout(() => {
            document.cartProxy = this
            document.addEventListener('click', this.onDocumentClick)
            document.addEventListener('keyup', this.onEscPress)
        }, 50)

        const gtm = useGtm()
        if (gtm && this._items.length) {
            const evt = {
                event: 'view_cart',
                ecommerce: {
                    value: parseFloat(this._subtotal.amount),
                    currency: this._items[0].currency,
                    items: this._items.map((item, idx) => ({
                        // eslint-disable-next-line camelcase
                        item_id: item.productId,
                        // eslint-disable-next-line camelcase
                        item_name: item.title,
                        index: idx,
                        price: item.priceValue,
                        quantity: item.quantity
                    }))
                }
            }
            gtm.trackEvent(evt)
        }
    },
    close(): void {
        this._open = false
        document.cartProxy = this
        document.removeEventListener('click', this.onDocumentClick)
        document.removeEventListener('keyup', this.onEscPress)
    },
    toggle(): void {
        if (this._open) {
            this.close()
        } else {
            this.open()
        }
    },

    // Close cart when clicking anywhere outside the open menu
    onDocumentClick(event: MouseEvent) {
        // TODO: find a better way to pass `this` into the event
        const proxy = event.currentTarget.cartProxy
        if (!proxy.isOpen()) return

        const clickedElement = event.target as HTMLElement
        const cart = document.getElementById('cart')
        if (clickedElement === cart || cart.contains(clickedElement)) return

        proxy.close()
    },

    // Close cart when pressing the escape key
    onEscPress(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            const proxy = event.currentTarget.cartProxy
            if (!proxy.isOpen()) return
            proxy.close()
        }
    }

})

const Collections = {
    SteamCodes: {
        gid: 'gid://shopify/Collection/104267284560'
    } as JBGShopify.CartProductCollection,
    Packs: {
        gid: 'gid://shopify/Collection/203693621382',
        storefrontPath: '/games/packs'
    } as JBGShopify.CartProductCollection,
    Bundles: {
        gid: 'gid://shopify/Collection/203693654150',
        storefrontPath: '/games/bundles'
    } as JBGShopify.CartProductCollection,
    Standalones: {
        gid: 'gid://shopify/Collection/203693686918',
        storefrontPath: '/games'
    } as JBGShopify.CartProductCollection,
    Merch: {
        gid: 'gid://shopify/Collection/203412570246',
        storefrontPath: '/shop/merch'
    } as JBGShopify.CartProductCollection,
    GiftCards: {
        gid: 'gid://shopify/Collection/203412537478',
        storefrontPath: '/shop/merch'
    } as JBGShopify.CartProductCollection
}

export const PromotionalProducts = {
    SurveyScramble: {
        slug: 'the-jackbox-survey-scramble',
        productId: 'gid://shopify/Product/7266062794886',
        variantId: 'gid://shopify/ProductVariant/41351595163782'
    },
    PartyPack11: {
        slug: 'the-jackbox-party-pack-11',
        productId: 'gid://shopify/Product/7424678232198',
        variantId: 'gid://shopify/ProductVariant/42183813333126'
    },
    BigBangBundle: {
        slug: 'the-jackbox-big-bang-bundle',
        productId: 'gid://shopify/Product/7449352601734',
        variantId: 'gid://shopify/ProductVariant/42391415619718'
    }
}


export class JBGShopify {
    private shopifyClient: Shopify
    private cart?
    private jbgCart: typeof ShoppingCart
    private locale: string
    private listrak: any
    private gtm: GtmSupport | undefined

    constructor(options: JBGShopify.InitOptions) {
        this.shopifyClient = new Shopify({
            shopName: options.shopName,
            apiToken: options.apiToken,
            locale: options.locale,
            countryCode: options.countryCode,
            enableCart: true
        })
        this.cart = this.shopifyClient.cart
        void this.fetchCart()
        this.jbgCart = ShoppingCart
        this.locale = options.locale
        this.listrak = options.listrak
        this.gtm = useGtm()
    }

    async fetchCart() {
        if (process.server) {
            console.warn('Unable to fetch cart on server ..')
            return
        }

        try {
            await this.cart?.refresh()
        } catch (error: unknown) {
            console.warn('Failed to fetch cart:', error)
        }
    }

    // add a give product variant in a given quantity to the cart. creates the cart if it doesn't exist
    public async addItemToCart(
        item: Shopify.Shopify.LineItem,
        qty?: number,
        product?: Shopify.Shopify.Product,
        variant?: Shopify.Shopify.Variant
    ): Promise<void> {
        await this.shopifyClient.cart.addItems([item])
        await this.refreshCart()

        if (this.gtm) {
            const evt = {
                event: 'add_to_cart',
                shop: {
                    itemId: item.merchandiseId,
                    quantity: item.quantity,
                    numItems: this.jbgCart.getQty(),
                    subtotal: this.jbgCart.getSubtotal(),
                    source: 'jackboxgames.com'
                },
                ecommerce: {}
            }
            if (variant && product) {
                if (!variant.compareAtPrice) variant.compareAtPrice = variant.price
                evt.ecommerce = {
                    currency: variant.price.currencyCode,
                    value: Number(variant.price.amount),
                    items: [
                        {
                            // eslint-disable-next-line camelcase
                            item_id: product.id,
                            // eslint-disable-next-line camelcase
                            item_name: product.title,
                            discount: Number(variant.compareAtPrice.amount) - Number(variant.price.amount),
                            // eslint-disable-next-line camelcase
                            item_variant: variant.id,
                            quantity: qty || 1,
                            price: Number(variant.price.amount)
                        }
                    ]
                }
            }
            this.gtm.trackEvent(evt)
        }
        this.updateListrakCart()
    }

    public async addItemToCartByVariantId(variantId: string, qty: number): Promise<void> {
        // create a line item
        const newLineItem: Shopify.Shopify.LineItem = {
            merchandiseId: variantId,
            quantity: qty,
            attributes: []
        }
        await this.shopifyClient.cart.addItems([newLineItem])
        await this.refreshCart()

        // Analytics
        if (this.gtm) {
            // Find newly added item
            const newItem: JBGShopify.DisplayItem = this.jbgCart.getCart().find((item) => item.variantId === variantId)
            this.gtm.trackEvent({
                event: 'add_to_cart',
                shop: {
                    itemId: newItem.id,
                    quantity: qty,
                    numItems: this.jbgCart.getQty(),
                    subtotal: this.jbgCart.getSubtotal(),
                    source: 'jackboxgames.com'
                },
                ecommerce: {
                    currency: newItem.currency,
                    value: (newItem.priceValue * newItem.quantity),
                    items: [
                        {
                            // eslint-disable-next-line camelcase
                            item_id: newItem.productId,
                            // eslint-disable-next-line camelcase
                            item_name: newItem.title,
                            // eslint-disable-next-line camelcase
                            item_variant: variantId,
                            quantity: qty,
                            price: newItem.priceValue
                        }
                    ]
                }
            })
        }
        this.updateListrakCart()
    }

    public async swapItemsByVariantId(oldVariantId: string, newVariantId: string, qty: number): Promise<void> {
        // Add/Remove To Cart
        const oldItem: JBGShopify.DisplayItem = this.jbgCart.getCart().find((item) => item.variantId === oldVariantId)
        await this.shopifyClient.cart.removeItem([oldItem.id])
        await this.addItemToCartByVariantId(newVariantId, qty)
        // refreshCart is handled in `addItemToCartByVariantId`

        // Analytics
        if (this.gtm) {
            // addItem tracking is handled in `addItemToCartByVariantId`
            this.gtmTrackRemoveItem(oldItem)
        }
    }

    public async createLineItemAndAddToCart(
        product?: Shopify.Shopify.Product,
        variant?: Shopify.Shopify.Variant,
        qty?: number
    ): Promise<void> {
        const variantId = variant?.id
        if (!variantId) {
            (console.error('couldn\'t get variant id'))
            return
        }
        // create a line item
        const lineItem: Shopify.Shopify.LineItem = {
            merchandiseId: variantId,
            quantity: qty,
            attributes: []
        }
        await this.addItemToCart(lineItem, 1, product, variant)
    }

    // takes a line item id and quantity, and updates that line item to have the new quantity
    public async updateItemQuantity(item: Shopify.Shopify.Line, quantity: number): Promise<void> {
        const prevItem: JBGShopify.DisplayItem = this.jbgCart.getCart().find((i) => i.id === item.id)
        if (!prevItem) {
            return
        }
        if (quantity === 0) {
            return this.removeItemFromCart(item.id)
        }
        await this.shopifyClient.cart.updateItems([{
            id: item.id,
            quantity
        }])
        await this.refreshCart()
        const ecommerce = {
            currency: prevItem.currency,
            value: (prevItem.priceValue * quantity),
            items: [
                {
                    // eslint-disable-next-line camelcase
                    item_id: prevItem.productId,
                    // eslint-disable-next-line camelcase
                    item_name: prevItem.title,
                    price: prevItem.priceValue,
                    quantity: quantity - prevItem.quantity
                }
            ]
        }

        if (this.gtm) {
            this.gtm.trackEvent({
                event: quantity > prevItem.quantity ? 'add_to_cart' : 'remove_from_cart',
                ecommerce,
                shop: {
                    itemId: prevItem.id,
                    quantity,
                    numItems: this.jbgCart.getQty(),
                    subtotal: this.jbgCart.getSubtotal(),
                    source: 'jackboxgames.com'
                }
            })
        }
        this.updateListrakCart()
    }

    // takes a line item id and removes it from the cart
    public async removeItemFromCart(lineItemId: string): Promise<void> {
        const item: JBGShopify.DisplayItem = this.jbgCart.getCart().find((item) => item.id === lineItemId)
        await this.shopifyClient.cart.removeItem([lineItemId])
        await this.refreshCart()

        if (this.gtm) {
            this.gtmTrackRemoveItem(item)
        }
        this.updateListrakCart()
    }

    private gtmTrackRemoveItem(item: JBGShopify.DisplayItem) {
        this.gtm.trackEvent({
            event: 'remove_from_cart',
            shop: {
                itemId: item.id,
                quantity: 0,
                numItems: this.jbgCart.getQty(),
                subtotal: this.jbgCart.getSubtotal(),
                source: 'jackboxgames.com'
            },
            ecommerce: {
                currency: item.currency,
                value: (item.priceValue * item.quantity),
                items: [
                    {
                        // eslint-disable-next-line camelcase
                        item_id: item.productId,
                        // eslint-disable-next-line camelcase
                        item_name: item.title,
                        price: item.priceValue,
                        quantity: item.quantity
                    }
                ]
            }
        })
    }

    private getSaleableCountryCode() {
        // gets country code from `cloudfront-viewer-country` cookie
        const isSaleableCountry = this.isSaleableCountry(currentCountry.code)
        if (!isSaleableCountry) {
            currentCountry.code = 'US'
        }
        return currentCountry.code
    }

    // Make sure detected country is one of our saleable countries, else fall back to US
    private isSaleableCountry(countryCode: string): boolean {
        if (!countryCode) return false
        let res = false
        // get saleable country list from json
        Object.entries(currencyCodeRegionMapJson).forEach((code) => {
            if (code[1].includes(countryCode)) {
                res = true
            }
        })
        return res
    }

    // Automatically set the cart currency based on the `cloudfront-viewer-country` cookie
    public async setCurrency(): Promise<void> {
        // based on an ISO 3166-1 alpha-2 country code
        const saleableCountryCode = this.getSaleableCountryCode()
        await this.shopifyClient.cart.setCountryCode(saleableCountryCode)
    }

    // get the redirect url to send the user to the checkout page
    public getCheckoutUrl(): string | null {
        if (this.cart) {
            return this.cart.checkoutUrl!
        }

        return null
    }

    // Fetch/update the cart, and return all line items as a displayable object
    public async refreshCart(): Promise<void> {
        await this.setCurrency()
        await this.fetchCart().then(() => {
            if (this.shopifyClient.cart) {
                // Update Cart
                const cart = this.shopifyClient.cart.lines
                const items: JBGShopify.DisplayItem[] = cart.map((line) => {
                    const price: string = line.merchandise?.price?.amount ?? '0'
                    const currency = line.merchandise?.price?.currencyCode ?? 'USD'
                    const image = line.merchandise?.image?.url
                    const sku = line.merchandise?.sku
                    const handle = line.merchandise?.product?.handle
                    const collections = line.merchandise?.product?.collections
                    const isSteamCode = collections.some((c) => c.id === Collections.SteamCodes.gid)
                    const options = line.merchandise?.selectedOptions?.reduce(
                        (msg: string[], val: {value: string}) => {
                            msg.push(val.value)
                            return msg
                        },
                        []
                    )
                    const optionString = options.join(' / ')

                    let productCollection: JBGShopify.CartProductCollection
                    if (collections.some((c) => c.id === Collections.Packs.gid)) {
                        productCollection = Collections.Packs
                    } else if (collections.some((c) => c.id === Collections.Standalones.gid)) {
                        productCollection = Collections.Standalones
                    } else if (collections.some((c) => c.id === Collections.Bundles.gid)) {
                        productCollection = Collections.Bundles
                    } else if (collections.some((c) => c.id === Collections.Merch.gid)) {
                        productCollection = Collections.Merch
                    } else if (collections.some((c) => c.id === Collections.GiftCards.gid)) {
                        productCollection = Collections.GiftCards
                    }

                    return {
                        id: line.id,
                        productId: line.merchandise?.product?.id,
                        variantId: line.merchandise?.id,
                        image,
                        title: line.merchandise?.product?.title ?? 'Product',
                        price: parseFloat(price).toLocaleString(this.locale, { style: 'currency', currency }),
                        currency,
                        priceValue: parseFloat(price),
                        quantity: line.quantity,
                        isSteamCode,
                        optionString,
                        sku,
                        handle,
                        collection: productCollection
                    }
                })

                // Update subtotal
                const price = this.cart.subtotal!
                const amount: string = price.amount

                const displayPrice = {
                    amount,
                    currencyCode: price.currencyCode
                } as JBGShopify.DisplayPrice

                // Update qty
                let quantity = 0
                items.forEach((lineItem) => {
                    quantity += lineItem.quantity
                })

                // Update Checkout Url
                const checkoutUrl: string = this.cart.checkoutUrl

                // Update cart
                this.jbgCart.updateCart(items, displayPrice, quantity, checkoutUrl)
            }
        })
    }

    public updateListrakCart(): void {
        // Send items to Listrak to track cart abandonment
        // Each time the cart is modified, the entire cart contents should be sent to Listrak
        // https://help.listrak.com/en/articles/4820523-javascript-website-integration-installation#h_87bdae2a90
        this.listrak((_ltk: any) => {
            const cart = this.jbgCart.getCart()
            if (cart.length === 0) {
                _ltk.SCA.ClearCart()
            } else {
                cart.forEach((lineItem) => {
                    let locale = ''
                    const baseUrl = useRequestURL().origin
                    const firstPathPart = useRequestURL().pathname.split('/')[1]
                    if (I18n.isSupported(firstPathPart)) {
                        locale = `/${firstPathPart}`
                    }

                    const productUrl = `${baseUrl}${locale}${lineItem.collection.storefrontPath}/${lineItem.handle}`

                    _ltk.SCA.AddItemWithLinks(
                        lineItem.sku, // sku
                        lineItem.quantity, // quantity
                        lineItem.price, // price
                        lineItem.title, // title
                        lineItem.image, // imageURL
                        productUrl // productURL
                    )
                })

                _ltk.SCA.Total = this.jbgCart.getSubtotal().amount
                _ltk.SCA.CartLink = this.jbgCart.getCheckoutUrl()
                _ltk.SCA.Submit()
            }
        })
    }

    // Gets a list of Products in a Collection
    public async fetchCollection(id: number): Promise<Shopify.Shopify.Collection> {
        const collection = await this.shopifyClient.query.getCollectionProducts(id)
        return collection
    }

    // Gets details for a single product
    public async fetchProduct(slug: string): Promise<Shopify.Shopify.Product> {
        const product = await this.shopifyClient.query.getProduct(slug)
        return product
    }

    public formatDecimalPlaces(priceAmount: string, locale: string) {
        const formattedPrice = new Intl.NumberFormat(
            locale,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        ).format(Number(priceAmount))
        return formattedPrice
    }
}

export namespace JBGShopify {
    export interface InitOptions {
        shopName: string
        apiToken: string
        locale: string
        countryCode: string
    }

    export interface DisplayItem {
        id: string
        productId?: string
        variantId?: string
        image?: string
        title: string
        price: string
        currency: string
        priceValue: number
        quantity: number
        isSteamCode: boolean
        optionString?: string
        sku?: string
        handle?: string
        collection?: CartProductCollection
    }

    export interface DisplayPrice {
        amount: string
        currencyCode: string
    }

    export interface CartProductCollection {
        gid: string,
        storefrontPath: string
    }

    export interface CollectionProduct {
        handle: string
        title: string
        variants: CollectionProductVariant
        featuredImage?: Image
    }

    export interface Image {
        url?: string
        height?: number
        width? : number
    }

    export interface CollectionProductVariant {
        id: string
        price: DisplayPrice
        compareAtPrice: DisplayPrice
        title: string
    }
}
